import * as React from 'react';
import type { Edge } from '@xyflow/react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useCanvasStore } from '@features/flows/state/canvas-store';
import LiquidGridPanel from './LiquidGridPanel';
import CodeEditor from '@shared/ui/adapters/shadcn/widgets/CodeEditor';
import { Loader2 } from 'lucide-react';

const tabs = [
  { id: 'liquid' as const, label: 'Liquid' },
  { id: 'jsonata' as const, label: 'JSONata' },
];

type TabId = (typeof tabs)[number]['id'];

function AutocastMapperDrawerInner() {
  const { open, edgeId, view } = useCanvasStore((state) => state.ui.mapper);
  const openMapper = useCanvasStore((state) => state.openMapper);
  const closeMapper = useCanvasStore((state) => state.closeMapper);
  const setJsonataOverride = useCanvasStore((state) => state.setJsonataOverride);
  const setEdgeJsonata = useCanvasStore((state) => state.setEdgeJsonata);

  const mappingEdge = useCanvasStore(
    React.useCallback(
      (state) => (edgeId ? state.graph.edges.find((edge) => edge.id === edgeId) : undefined),
      [edgeId],
    ),
  ) as Edge | undefined;

  const data = (mappingEdge?.data ?? {}) as Record<string, unknown>;
  const mappingLiquid = data.mappingLiquid as Record<string, string> | undefined;
  const jsonata = typeof data.mappingJSONata === 'string' ? data.mappingJSONata : '';
  const jsonataOverride = Boolean(data.mappingJsonataOverride);
  const llmRunning = Boolean(data.mappingLLMRunning);
  const llmError = typeof data.mappingLLMError === 'string' ? data.mappingLLMError : null;

  const [jsonataDraft, setJsonataDraft] = React.useState(jsonata);

  React.useEffect(() => {
    setJsonataDraft(jsonata);
  }, [jsonata, jsonataOverride, open]);

  const currentView: TabId = (view as TabId | undefined) ?? 'liquid';

  const handleTabClick = (tab: TabId) => {
    if (!edgeId) return;
    openMapper(edgeId, tab);
  };

  const handleOverrideChange = (checked: boolean) => {
    if (!edgeId) return;
    setJsonataOverride(edgeId, checked);
  };

  const handleJsonataChange = (value?: string) => {
    const next = value ?? '';
    setJsonataDraft(next);
    if (jsonataOverride && edgeId) {
      setEdgeJsonata(edgeId, next);
    }
  };

  const handleEditorBlur = () => {
    if (!jsonataOverride || !edgeId) return;
    setEdgeJsonata(edgeId, jsonataDraft ?? '');
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) closeMapper();
      }}
    >
      <SheetContent side="right" className="w-[98vw] max-w-[98vw] sm:w-[1400px] sm:max-w-[95vw] xl:max-w-[1600px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            Mapping
            {llmRunning && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Synthesizing…
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {llmError && (
          <div className="mt-4 rounded-md border border-destructive/60 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {llmError}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2 px-4">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              size="sm"
              variant={currentView === tab.id ? 'default' : 'outline'}
              onClick={() => handleTabClick(tab.id)}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        <div className="mt-4 space-y-4 px-4 pb-6">
          <div className="rounded-md border bg-muted/30 p-3 text-xs">
            <div className="font-semibold text-muted-foreground">Generated JSONata</div>
            <div className="mt-2 whitespace-pre-wrap break-all text-[13px]">
              {jsonata ? jsonata : '// JSONata expression will appear once generated.'}
            </div>
          </div>

          {currentView === 'liquid' && edgeId && <LiquidGridPanel edgeId={edgeId} />}

          {currentView === 'jsonata' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox id="mapping-jsonata-override" checked={jsonataOverride} onCheckedChange={(next) => handleOverrideChange(Boolean(next))} />
                <Label htmlFor="mapping-jsonata-override" className="text-xs">
                  Manual JSONata override
                </Label>
              </div>
              <CodeEditor
                value={jsonataOverride ? jsonataDraft : jsonata}
                readOnly={!jsonataOverride}
                onChange={handleJsonataChange}
                onBlur={handleEditorBlur}
                options={{
                  language: 'jsonata',
                  height: 360,
                }}
              />
              {!jsonataOverride && (
                <p className="text-xs text-muted-foreground">
                  Generated from Liquid mappings. Turn on the override to edit manually.
                </p>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default React.memo(AutocastMapperDrawerInner);
